import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import PhoneInput from 'react-phone-number-input';
import {
  isValidPhoneNumber,
  formatPhoneNumber,
} from 'react-phone-number-input';
import constants from '../constants';
import { countries } from '../constants/countries';
import {
  US_STATE_CODES,
  US_TERRITORY_AND_OTHER_OPTIONS,
} from '../constants/usStates';
import { storeIds } from '../constants/stores';
import { getMonths, getDays, getYears } from '../utils/dateUtils';
import { getExpectedPhoneDigits } from '../utils/phoneUtils';
import { useTestState } from '../context/TestContext';
import useTracking from '../hooks/useTracking';
import {
  hmacEncode,
  isNetworkError,
  createNetworkError,
  createApiError,
  sanitizePayload,
} from '../utils';
import { useLogError } from '../hooks/useLogError';
import useTrackGAConnector from '../hooks/useTrackGAConnector';
import ErrorMessage from '../components/error-message';
import { ApiError } from '../types';

const EMAIL_REGEX =
  /^(([^<>()[\].,;:\s@']+(\.[^<>()[\].,;:\s@']+)*)|('.+'))@(([^<>()[\].,;:\s@']+\.)+[^<>()[\].,;:\s@']{2,})$/i;

interface FormData {
  firstname: string;
  lastname: string;
  email: string;
  phone: string;
  day: string;
  month: string;
  year: string;
  dob: string;
  state?: string;
  storeId?: string | null;
}

const Demographics = ({ rht }: { rht: boolean }) => {
  const navigate = useNavigate();
  const { trackEvent, identifyUser } = useTracking();
  const { isGAConnectorEventTracked, trackGAConnectorEvent } =
    useTrackGAConnector();
  const { logError } = useLogError();

  const {
    register,
    watch,
    setValue,
    formState: { errors },
    handleSubmit,
    setError,
    clearErrors,
  } = useForm({
    defaultValues: {
      firstname: '',
      lastname: '',
      email: '',
      phone: '',
      month: '',
      day: '',
      year: '',
      dob: '',
      state: '',
      storeId: '',
    },
  });

  const { dispatch } = useTestState();
  const [loading, setLoading] = useState(false);
  const [isPageLoadTracked, setIsPageLoadTracked] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [error, setApiError] = useState<ApiError | null>(null);
  const [retryData, setRetryData] = useState<FormData | null>(null);
  const [selectedCountry, setSelectedCountry] = useState('US');
  const [phoneNumber, setPhoneNumber] = useState('');

  const { t } = useTranslation('demographics');

  useEffect(() => {
    dispatch({ type: 'UPDATE_PROGRESS', payload: 20 });
    register('dob');
    register('phone', { validate: validatePhoneNumber });
  }, [dispatch, register]);

  useEffect(() => {
    if (!isPageLoadTracked) {
      trackEvent('JHT_DEMOGRAPHICS_PAGE_LOAD');
      setIsPageLoadTracked(true);
    }

    if (!isGAConnectorEventTracked) {
      trackGAConnectorEvent();
    }
  }, [
    trackEvent,
    isGAConnectorEventTracked,
    trackGAConnectorEvent,
    isPageLoadTracked,
  ]);

  useEffect(() => {
    const dob = `${watch('year')}-${watch('month')}-${watch('day')}`;
    setValue('dob', dob);
  }, [watch, setValue]);

  const calculateAge = (dob: string) => {
    const dobDate = new Date(dob);
    const diff = Date.now() - dobDate.getTime();
    const ageDate = new Date(diff);
    return Math.abs(ageDate.getUTCFullYear() - 1970);
  };

  const validatePhoneNumber = (value: string) => {
    if (!value) return t('phone-required');

    if (!isValidPhoneNumber(value)) {
      return t('phone-invalid');
    }

    return true;
  };

  const activityTypeId = 'JHT Web Create Contact';

  const formData2Activity = (formData: FormData) => {
    const dobFormatted = `${formData.year}-${formData.month}-${formData.day}`;

    const cleanPhone = formData.phone.replace(/[^\d+]/g, '');

    const contactData = {
      firstname: formData.firstname,
      lastname: formData.lastname,
      email: formData.email,
      phone: cleanPhone,
    };
    const patientData = {
      first_name__c: formData.firstname,
      last_name__c: formData.lastname,
      date_of_birth__c: dobFormatted,
    };
    const activityFacilityData = formData.storeId
      ? {
          id: formData.storeId,
        }
      : null;
    const activityData = {
      contact: contactData,
      patient__c: patientData,
      activitytypeid: activityTypeId,
      activityfacility: activityFacilityData,
    };
    return JSON.stringify(sanitizePayload(activityData));
  };

  const isFormValid = (data: FormData) => {
    setFormSubmitted(true);
    if (!data.month || !data.day || !data.year) {
      setError('dob', {
        type: 'manual',
        message: 'Date of Birth is incomplete.',
      });
      return false;
    } else {
      clearErrors('dob');
      const age = calculateAge(`${data.year}-${data.month}-${data.day}`);
      if (age < 18) {
        setError('dob', {
          type: 'manual',
          message: 'You must be at least 18 years old',
        });
        return false;
      }
    }

    if (!rht) {
      if (!data.state) {
        setError('state', {
          type: 'manual',
          message: 'Please select a state.',
        });
        return false;
      } else {
        clearErrors('state');
      }
    }

    if (rht) {
      if (!data.storeId) {
        setError('storeId', {
          type: 'manual',
          message: 'Please select a store location.',
        });
        return false;
      } else {
        clearErrors('storeId');
      }
    }

    return true;
  };

  const onSubmit = async (data: FormData) => {
    setFormSubmitted(true);

    // continue if form is valid
    if (!isFormValid(data)) return;

    setIsButtonDisabled(true);
    setLoading(true);
    setApiError(null);

    // Store the form data for potential retry
    setRetryData(data);

    const activityData = formData2Activity(data);
    const signature = await hmacEncode(activityData, constants['hmacSecret']);

    try {
      const response = await fetch(constants['apiBaseUrl'], {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-AUD-Signature': signature,
        },
        body: activityData,
      });

      const responseData = await response.json();
      setLoading(false);

      if (!responseData?.contact?.id) {
        setIsButtonDisabled(false);
        setApiError(
          createApiError(
            `Failed to POST to ${constants['apiBaseUrl']} on ${activityTypeId}: ${responseData}. Please try again.`,
          ),
        );
      } else {
        setRetryData(null);

        // Handle successful response
        dispatch({
          type: 'UPDATE_DEMOGRAPHICS',
          payload: {
            contactid: responseData.contact.id,
            patientid: responseData.patient__c.id,
            email: data.email,
            dob: `${data.year}-${data.month}-${data.day}`,
            nameonresult: `${data.firstname} ${data.lastname}`,
            createdAtState: data.state || undefined,
          },
        });

        if (rht && data.storeId) {
          dispatch({ type: 'UPDATE_FACILITYID', payload: data.storeId });
        }

        if (process.env.REACT_APP_USE_SKIP_SEGMENT === 'false') {
          identifyUser(responseData.contact.id);
        }
        trackEvent('JHT_SUBMIT_DEMOGRAPHICS');
        navigate(rht ? '/headphones' : '/device');
      }
    } catch (error) {
      setLoading(false);
      setIsButtonDisabled(false);

      if (isNetworkError(error)) {
        setApiError(createNetworkError());
      } else {
        setApiError(
          createApiError('An unexpected error occurred. Please try again.'),
        );
        logError(error);
      }
    }
  };

  const handleRetry = async () => {
    if (retryData) {
      setApiError(null);
      await onSubmit(retryData);
    }
  };

  return (
    <div className="demographics">
      <h1 className="top-title-text">{t('top-title')}</h1>

      <form className="form-content" onSubmit={handleSubmit(onSubmit)}>
        <label htmlFor="firstname">{t('first-name')}</label>
        <input
          className={errors.firstname ? 'field-error' : ''}
          {...register('firstname', { required: t('first-name-required') })}
        />
        <p className="error-text">
          {errors.firstname && errors.firstname.message}
        </p>

        <label htmlFor="lastname">{t('last-name')}</label>
        <input
          className={errors.lastname ? 'field-error' : ''}
          {...register('lastname', { required: t('last-name-required') })}
        />
        <p className="error-text">
          {errors.lastname && errors.lastname.message}
        </p>

        <label htmlFor="email">{t('email')}</label>
        <input
          className={errors.email ? 'field-error' : ''}
          {...register('email', {
            required: t('email-required'),
            pattern: { value: EMAIL_REGEX, message: t('email-invalid') },
          })}
          aria-invalid={errors.email ? 'true' : 'false'}
        />
        <p className="error-text">{errors.email && errors.email.message}</p>

        <label htmlFor="phone">{t('phone')}</label>
        <div className="phone-input-container">
          <select
            className="phone-country-select"
            value={selectedCountry}
            onChange={(e) => {
              const countryCode = e.target.value;
              setSelectedCountry(countryCode);
              const countryData = countries.find((c) => c.code === countryCode);
              if (countryData && phoneNumber) {
                const fullPhone = countryData.dialCode + phoneNumber;
                setValue('phone', fullPhone);
              }
            }}
          >
            {countries.map((country) => (
              <option key={country.code} value={country.code}>
                {country.flag} {country.dialCode} {country.name}
              </option>
            ))}
          </select>
          <input
            className={`phone-input-field ${errors.phone ? 'field-error' : ''}`}
            value={phoneNumber}
            onChange={(e) => {
              const inputValue = e.target.value;
              const digitsOnly = inputValue.replace(/\D/g, '');
              const expectedDigits = getExpectedPhoneDigits(selectedCountry);

              if (digitsOnly.length > expectedDigits) return;

              const countryData = countries.find(
                (c) => c.code === selectedCountry,
              );

              if (countryData && digitsOnly) {
                const fullPhone = countryData.dialCode + digitsOnly;
                try {
                  const formatted = formatPhoneNumber(fullPhone);
                  if (formatted) {
                    const localFormatted = formatted
                      .replace(countryData.dialCode, '')
                      .trim();
                    setPhoneNumber(localFormatted);
                    setValue('phone', fullPhone);
                  } else {
                    setPhoneNumber(digitsOnly);
                    setValue('phone', fullPhone);
                  }
                } catch (error) {
                  setPhoneNumber(digitsOnly);
                  setValue('phone', fullPhone);
                }
              } else {
                setPhoneNumber(digitsOnly);
                if (countryData) {
                  setValue('phone', countryData.dialCode + digitsOnly);
                }
              }
            }}
            onBlur={(e) => {
              const countryData = countries.find(
                (c) => c.code === selectedCountry,
              );
              if (countryData && phoneNumber) {
                const fullPhone = countryData.dialCode + phoneNumber;
                try {
                  const formatted = formatPhoneNumber(fullPhone);
                  if (formatted) {
                    const localFormatted = formatted
                      .replace(countryData.dialCode, '')
                      .trim();
                    setPhoneNumber(localFormatted);
                    setValue('phone', fullPhone);
                  }
                } catch (error) {
                  console.log(error);
                }
              }
            }}
            placeholder="(555) 123-4567"
            aria-invalid={errors.phone ? 'true' : 'false'}
          />
        </div>
        <p className="error-text">{errors.phone && errors.phone.message}</p>

        <label htmlFor="dob">{t('dob')}</label>
        <div style={{ display: 'flex', gap: '10px' }}>
          <select
            className={formSubmitted && !watch('month') ? 'field-error' : ''}
            style={{ width: '40%' }}
            {...register('month', { onChange: () => setFormSubmitted(false) })}
            defaultValue=""
          >
            <option value="" disabled>
              {t('month')}
            </option>
            {getMonths(t).map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <select
            className={formSubmitted && !watch('day') ? 'field-error' : ''}
            style={{ width: '24%' }}
            {...register('day', { onChange: () => setFormSubmitted(false) })}
            defaultValue=""
          >
            <option value="" disabled>
              {t('day')}
            </option>
            {getDays().map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <select
            className={formSubmitted && !watch('year') ? 'field-error' : ''}
            style={{ width: '36%' }}
            {...register('year', { onChange: () => setFormSubmitted(false) })}
            defaultValue=""
          >
            <option value="" disabled>
              {t('year')}
            </option>
            {getYears().map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <p className="error-text">{errors.dob && errors.dob.message}</p>

        {!rht ? (
          <>
            <label htmlFor="state">{t('state')}*</label>
            <select
              className={errors.state ? 'field-error' : ''}
              {...register('state', {
                required: t('state-required'),
              })}
              defaultValue=""
            >
              <option value="" disabled>
                {t('select-state')}
              </option>
              {US_STATE_CODES.map((stateCode) => (
                <option key={stateCode} value={stateCode}>
                  {stateCode}
                </option>
              ))}
              {US_TERRITORY_AND_OTHER_OPTIONS.map(({ value, labelKey }) => (
                <option key={value} value={value}>
                  {t(labelKey)}
                </option>
              ))}
            </select>
            <p className="error-text">
              {errors.state && errors.state.message}
            </p>
          </>
        ) : null}

        {rht ? (
          <>
            <label htmlFor="storeId">{t('store-location')}</label>
            <select
              className={errors.storeId ? 'field-error' : ''}
              {...register('storeId', {
                required: t('store-location-required'),
              })}
              defaultValue=""
            >
              <option value="" disabled>
                {t('select-location')}
              </option>
              {storeIds.map((location) => (
                <option key={location.value} value={location.value}>
                  {location.label}
                </option>
              ))}
            </select>
            <p className="error-text">
              {errors.storeId && errors.storeId.message}
            </p>
          </>
        ) : null}

        <p className="consent-text">
          {t('consent-text')}{' '}
          <a
            href="https://audicus.com/privacy-policy"
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('privacy-policy')}
          </a>{' '}
          {t('and')}{' '}
          <a
            href="https://audicus.com/terms-of-service"
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('terms-of-service')}
          </a>
          .
        </p>

        <input
          type="submit"
          className="link-button"
          value={loading ? t('submitting') : t('submit')}
          disabled={isButtonDisabled}
        />

        {error && (
          <ErrorMessage
            type={error.type}
            message={error.message}
            onRetry={error.retryable ? handleRetry : undefined}
            retryText={t('retry')}
          />
        )}
      </form>
    </div>
  );
};

export default Demographics;
